import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Box, Button, Image } from '@chakra-ui/react';
import { encodeHtmlEntities, mergeAttributes, Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { FiEdit2 } from 'react-icons/fi';

export interface MarkdownImageAttrs {
  src: string;
  alt: string;
  title: string | null;
  width: string | null;
  height: string | null;
}

export interface MarkdownImageEditValues {
  src: string;
  width: string;
  height: string;
}

export interface MarkdownImageEditRequest {
  mode: 'edit' | 'insert';
  initialValues: MarkdownImageEditValues;
  onCommit: (values: MarkdownImageEditValues) => void | Promise<void>;
}

interface MarkdownImageNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

function normalizeImageString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value).trim();
  }

  return '';
}

function normalizeNullableImageString(value: unknown): string | null {
  const normalized = normalizeImageString(value);
  return normalized.length > 0 ? normalized : null;
}

export function markdownImageDimensionToCssSize(
  value: string | null,
): string | undefined {
  const normalized = value?.trim();
  if (!normalized) {
    return undefined;
  }

  return /^\d+(?:\.\d+)?$/.test(normalized) ? `${normalized}px` : normalized;
}

export function normalizeMarkdownImageAttrs(
  attrs: unknown,
): MarkdownImageAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    src: normalizeImageString(rec.src),
    alt: normalizeImageString(rec.alt),
    title: normalizeNullableImageString(rec.title),
    width: normalizeNullableImageString(rec.width),
    height: normalizeNullableImageString(rec.height),
  };
}

export function markdownImageAttrsToEditValues(
  attrs: MarkdownImageAttrs,
): MarkdownImageEditValues {
  return {
    src: attrs.src,
    width: attrs.width ?? '',
    height: attrs.height ?? '',
  };
}

export function markdownImageEditValuesToAttrs(
  values: MarkdownImageEditValues,
  existing?: Pick<MarkdownImageAttrs, 'alt' | 'title'>,
): MarkdownImageAttrs {
  return {
    src: normalizeImageString(values.src),
    alt: existing?.alt ?? '',
    title: existing?.title ?? null,
    width: normalizeNullableImageString(values.width),
    height: normalizeNullableImageString(values.height),
  };
}

export function renderMarkdownImage(attrs: MarkdownImageAttrs): string {
  const src = attrs.src.trim();
  if (!src) {
    return '';
  }

  const pairs: Array<[string, string | null]> = [
    ['src', src],
    ['alt', attrs.alt.trim() || null],
    ['width', attrs.width?.trim() || null],
    ['height', attrs.height?.trim() || null],
    ['title', attrs.title?.trim() || null],
  ];
  const serializedAttrs = pairs
    .filter(([, value]) => value != null)
    .map(([name, value]) => `${name}="${encodeHtmlEntities(value ?? '')}"`)
    .join(' ');

  return `<img ${serializedAttrs} />`;
}

export function parseMarkdownImageToken(token: {
  href?: unknown;
  text?: unknown;
  title?: unknown;
}): MarkdownImageAttrs | null {
  const href = typeof token.href === 'string' ? token.href.trim() : '';
  if (!href) {
    return null;
  }

  return {
    src: href,
    alt: typeof token.text === 'string' ? token.text : '',
    title: typeof token.title === 'string' ? token.title : null,
    width: null,
    height: null,
  };
}

function MarkdownImageNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownImageNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeMarkdownImageAttrs(node.attrs);
  const editValues = markdownImageAttrsToEditValues(attrs);

  const handleEdit = () => {
    context.onRequestImageEdit?.({
      mode: 'edit',
      initialValues: editValues,
      onCommit: async (next) => {
        updateAttributes(
          markdownImageEditValuesToAttrs(next, {
            alt: attrs.alt,
            title: attrs.title,
          }),
        );
      },
    });
  };

  return (
    <NodeViewWrapper
      as="span"
      className={
        selected
          ? 'markdown-image-node markdown-image-node--selected'
          : 'markdown-image-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <Box
        as="span"
        className="markdown-image-node__frame"
        position="relative"
        display="inline-block"
        maxW="100%"
      >
        <Image
          className="markdown-image-node__image"
          src={attrs.src}
          alt={attrs.alt}
          title={attrs.title ?? undefined}
          style={{
            width: markdownImageDimensionToCssSize(attrs.width),
            height: markdownImageDimensionToCssSize(attrs.height),
          }}
          maxW="100%"
          objectFit="contain"
          borderRadius="md"
        />
        <Button
          className="markdown-image-node__edit"
          type="button"
          size="xs"
          variant="solid"
          colorPalette="gray"
          aria-label="Edit image"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={handleEdit}
          disabled={!context.onRequestImageEdit}
        >
          <FiEdit2 />
        </Button>
      </Box>
    </NodeViewWrapper>
  );
}

export const MarkdownImageNode = Node.create({
  name: 'image',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      src: {
        default: '',
      },
      alt: {
        default: '',
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (element) => {
          if (!(element instanceof HTMLImageElement)) {
            return false;
          }

          const src = element.getAttribute('src') ?? '';
          if (!src.trim()) {
            return false;
          }

          return {
            src,
            alt: element.getAttribute('alt') ?? '',
            title: element.getAttribute('title'),
            width: element.getAttribute('width'),
            height: element.getAttribute('height'),
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = normalizeMarkdownImageAttrs(node.attrs);
    const imageAttributes: Record<string, unknown> = {
      src: attrs.src,
    };

    if (attrs.alt) {
      imageAttributes.alt = attrs.alt;
    }

    if (attrs.width) {
      imageAttributes.width = attrs.width;
    }

    if (attrs.height) {
      imageAttributes.height = attrs.height;
    }

    if (attrs.title) {
      imageAttributes.title = attrs.title;
    }

    return [
      'img',
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        imageAttributes,
      ),
    ];
  },

  parseMarkdown(token, helpers) {
    const attrs = parseMarkdownImageToken(token);
    if (!attrs) {
      return [];
    }

    return helpers.createNode('image', attrs, []);
  },

  renderMarkdown(node) {
    return renderMarkdownImage(normalizeMarkdownImageAttrs(node.attrs));
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownImageNodeView);
  },
});
